import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import MarcoReporte, {
  TD,
  TD_NUM,
  TH,
  TarjetaTotal,
} from '../../components/reportes/MarcoReporte';
import {
  CLASE_INPUT,
  Campo,
  Casilla,
  SelectCategoria,
} from '../../components/reportes/FiltrosComunes';
import { useConciliacion } from '../../hooks/useReportes';
import { formatearPrecio } from '../../utils/facturaCalculos';
import { obtenerFechaActualLocal } from '../../utils/dateUtils';
import BloqueLectura from '../../components/reportes/BloqueLectura';
import { EtiquetasDestacado, useDestacados } from '../../components/reportes/destacados';

/**
 * Reporte 2 — Conciliación de movimientos (PROMPT_N°054).
 *
 * La columna que importa es la última: **Diferencia**. Cero significa que
 * cada gramo que entró o salió dejó su fila en `movimientos_stock`.
 * Distinto de cero significa que el stock se movió por fuera, y eso hay
 * que investigarlo — por eso la fila se pinta y cada una enlaza al kardex
 * con el ingrediente y el rango ya filtrados.
 */

const primerDiaDelMes = () => `${obtenerFechaActualLocal().slice(0, 7)}-01`;

export default function ReporteConciliacion() {
  const [params, setParams] = useSearchParams();

  const poner = (clave: string, valor: string | boolean) => {
    const p = new URLSearchParams(params);
    const v = String(valor);
    if (v === '' || v === 'undefined') p.delete(clave);
    else p.set(clave, v);
    setParams(p, { replace: true });
  };

  const desde = params.get('desde') ?? primerDiaDelMes();
  const hasta = params.get('hasta') ?? obtenerFechaActualLocal();

  /**
   * Aplica el filtro de una frase clickeable y baja a la tabla. Los filtros
   * viven en la query string (N°053), así que basta con escribirlos ahí:
   * el reporte se vuelve a consultar solo y el link queda compartible.
   */
  const aplicarFiltroDeFrase = (
    filtro: Record<string, string | number | boolean>
  ) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(filtro)) p.set(k, String(v));
    setParams(p, { replace: true });
    document
      .getElementById('tabla-reporte')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filtros = useMemo(
    () => ({
      desde,
      hasta,
      categoria_id: params.get('categoria_id') ?? undefined,
      ingrediente_id: params.get('ingrediente_id') ?? undefined,
      solo_con_movimientos: params.get('solo_con_movimientos') !== 'false',
    }),
    [desde, hasta, params]
  );

  const { data, isLoading, error } = useConciliacion(filtros);
  const filas = data?.filas ?? [];
  const conDiferencia = data?.total.con_diferencia ?? 0;
  const destacados = useDestacados(data?.lectura);

  const num = (n: number) =>
    n === 0 ? '—' : n.toLocaleString('es-CL', { maximumFractionDigits: 3 });

  return (
    <MarcoReporte
      nombre="conciliacion"
      titulo="Conciliación de movimientos"
      descripcion="Saldo reconstruido desde los movimientos, contra el stock que dice el sistema."
      filtros={filtros}
      cargando={isLoading}
      error={error}
      vacio={!isLoading && filas.length === 0}
      mensajeVacio="No hubo movimientos en el período."
      lectura={
        <BloqueLectura
          lectura={data?.lectura}
          ayuda="conciliacion"
          cargando={isLoading}
          onFiltro={aplicarFiltroDeFrase}
        />
      }
      controles={
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Campo etiqueta="Desde">
            <input
              type="date"
              className={CLASE_INPUT}
              value={desde}
              onChange={(e) => poner('desde', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Hasta">
            <input
              type="date"
              className={CLASE_INPUT}
              value={hasta}
              onChange={(e) => poner('hasta', e.target.value)}
            />
          </Campo>
          <SelectCategoria
            valor={params.get('categoria_id') ?? ''}
            onChange={(v) => poner('categoria_id', v)}
          />
          <div className="md:pt-5">
            <Casilla
              etiqueta="Sólo con movimientos"
              marcada={filtros.solo_con_movimientos}
              onChange={(v) => poner('solo_con_movimientos', v)}
              ayuda="Apagado muestra también los que no se movieron"
            />
          </div>
        </div>
      }
      aviso={
        data ? (
          conDiferencia > 0 ? (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-900 dark:text-red-200 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>
                <strong>
                  {conDiferencia} ingrediente{conDiferencia === 1 ? '' : 's'} con
                  diferencia.
                </strong>{' '}
                El stock se movió sin dejar registro en los movimientos. Hay que
                revisar cada caso antes de cerrar el período.
              </span>
            </div>
          ) : (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 p-3 text-sm text-emerald-900 dark:text-emerald-200 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <span>
                Sin diferencias: el stock de los {data.total.ingredientes}{' '}
                ingredientes coincide con sus movimientos.
              </span>
            </div>
          )
        ) : null
      }
      totales={
        data ? (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <TarjetaTotal
              etiqueta="Ingredientes"
              valor={String(data.total.ingredientes)}
            />
            <TarjetaTotal
              etiqueta="Entradas"
              valor={`$${formatearPrecio(data.total.valor_entradas)}`}
              tono="bueno"
            />
            <TarjetaTotal
              etiqueta="Salidas"
              valor={`$${formatearPrecio(data.total.valor_salidas)}`}
              tono="alerta"
            />
            <TarjetaTotal
              etiqueta="Con diferencia"
              valor={String(conDiferencia)}
              tono={conDiferencia > 0 ? 'malo' : 'bueno'}
            />
          </div>
        ) : null
      }
    >
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900/40">
          <tr>
            <th className={TH}>Ingrediente</th>
            <th className={`${TH} text-right`}>Inicial</th>
            <th className={`${TH} text-right`}>Factura</th>
            <th className={`${TH} text-right`}>Rec. OC</th>
            <th className={`${TH} text-right`}>Ent. bodega</th>
            <th className={`${TH} text-right`}>Reversos</th>
            <th className={`${TH} text-right`}>Sal. bodega</th>
            <th className={`${TH} text-right`}>Producción</th>
            <th className={`${TH} text-right`}>Merma</th>
            <th className={`${TH} text-right`}>Ajustes</th>
            <th className={`${TH} text-right`}>Calculado</th>
            <th className={`${TH} text-right`}>Sistema</th>
            <th className={`${TH} text-right`}>Diferencia</th>
            <th className={TH} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {filas.map((f) => {
            const descuadra = f.diferencia !== 0;
            return (
              <tr
                key={f.id}
                className={
                  descuadra
                    ? 'bg-red-50 dark:bg-red-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                }
              >
                <td className={TD}>
                  <div className="font-medium">
                    {f.nombre}
                    <EtiquetasDestacado tipos={destacados.get(f.id)} />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {f.categoria ?? 'Sin categoría'} · {f.unidad}
                  </div>
                </td>
                <td className={TD_NUM}>{num(f.saldo_inicial)}</td>
                <td className={TD_NUM}>{num(f.e_factura)}</td>
                <td className={TD_NUM}>{num(f.e_oc)}</td>
                <td className={TD_NUM}>{num(f.e_bodega)}</td>
                <td className={TD_NUM}>{num(f.e_reverso)}</td>
                <td className={TD_NUM}>{num(f.s_bodega)}</td>
                <td className={TD_NUM}>{num(f.s_produccion)}</td>
                <td className={TD_NUM}>{num(f.s_merma)}</td>
                <td className={TD_NUM}>{num(f.ajustes)}</td>
                <td className={`${TD_NUM} font-medium`}>
                  {f.saldo_final_calculado.toLocaleString('es-CL', {
                    maximumFractionDigits: 3,
                  })}
                </td>
                <td className={TD_NUM}>
                  {f.saldo_sistema.toLocaleString('es-CL', {
                    maximumFractionDigits: 3,
                  })}
                </td>
                <td
                  className={`${TD_NUM} font-bold ${
                    descuadra
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {descuadra
                    ? f.diferencia.toLocaleString('es-CL', {
                        maximumFractionDigits: 3,
                      })
                    : '0'}
                </td>
                <td className={TD}>
                  {/* Al kardex con el ingrediente y el rango ya puestos:
                      desde una diferencia, lo primero que se quiere es ver
                      los movimientos que la produjeron. */}
                  <Link
                    to={`/movimientos?ingrediente_id=${f.id}&desde=${desde}&hasta=${hasta}`}
                    className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    title="Ver movimientos de este ingrediente en el período"
                  >
                    Kardex
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </MarcoReporte>
  );
}
